// Episode 1 cinematic panels, boss dialogue and level text.
//
// (c) Copyright 2026 Liminal HQ, Scott Morris
// SPDX-License-Identifier: Apache-2.0 OR MIT

import type { BeatScene } from '@lieutenant-fizz/engine/story-beats';
import type { RoomName } from './audio/rooms';

// Episode 1 text: the eight scenes of the opening cinematic and the four of the ending, each a place
// and its beats (the author's break points; the screen packs as many consecutive beats of a scene as
// fit into a page, see `story-measure.ts`), then dialogue and level blurbs.
// docs/STORY.md mirrors the cinematic text.

/** The beat of the Liftoff scene that opens the hatch, where the launch begins (see `Cinematic.launch`). */
export const LIFTOFF_BEAT = 1;

export const CINE: BeatScene[] = [
  {
    place: 'The backyard',
    beats: [
      "One night, Ben Blaze's cousin Billy didn't come home.",
      'Ben checked the treehouse where they always played.',
      "Billy wasn't there — but a hatch between the roots was hanging open.",
      'Warm light was spilling out.',
    ],
  },
  {
    place: 'Under the treehouse',
    beats: [
      'A ladder led down to a secret lab.',
      'Blinking consoles. Blueprints pinned to the walls.',
      'And a prototype spaghetti with meatballs flying saucer on a launch pad.',
      "On the workbench lay Billy's note — a map, with an X and a planet name beside it.",
      "“That must be where he's gone.”",
    ],
  },
  {
    place: 'Liftoff',
    // The page that opens the hatch always starts at its beat, so the launch waits for exactly that text.
    breaks: [LIFTOFF_BEAT],
    beats: [
      'Ben put on his bicycle helmet, climbed aboard and hit the big Engage button.',
      'A hatch in the lawn slid open.',
      'The saucer rose past the treehouse into the night.',
    ],
  },
  {
    place: 'The stratosphere',
    beats: [
      "The G-force pressed Ben deep into the noodle-upholstered captain's chair.",
      'The saucer tore through the stratosphere.',
      'Marinara-scented exhaust plumed behind him,',
      'painting a crimson streak across the night sky.',
      'He gripped the steering yoke —',
      'which felt suspiciously like a giant, hardened breadstick —',
      'and steadied his breathing.',
    ],
  },
  {
    place: 'Past the Moon',
    beats: [
      'On the dashboard, pinned beneath a blinking, meatball-shaped radar dial,',
      "was Billy's note.",
      'Ben tapped the X on the crinkled parchment.',
      'The destination was boldly scribbled: Planet Zargoth.',
      'A remote outpost on the outer galactic rim.',
      'Exactly the kind of dangerous, alien world Billy would wander off to.',
    ],
  },
  {
    place: 'Deep space',
    beats: [
      'Ben engaged the autopilot and popped open the glovebox:',
      'a crinkled bag of Cheezies and a cold can of Canadian cream soda.',
      'The loud, satisfying crunch of the bright orange snacks echoed in the quiet cabin.',
      'The stars stretched into streaks of blinding white light outside the viewport.',
    ],
  },
  {
    place: 'Planet Zargoth',
    beats: [
      'Hours later, the navigation console chimed a whimsical, retro 8-bit melody.',
      'Planet Zargoth loomed through the windshield —',
      'a swirling marble of neon green and deep purple clouds.',
      'Ben wiped his cheesy fingers on his jeans, grabbed the yoke,',
      'and manoeuvred the pasta-themed vessel down through the upper atmosphere.',
    ],
  },
  {
    place: 'The crystal forest',
    beats: [
      'He touched down with a heavy, saucy thud in the centre of a glowing, crystalline forest.',
      'The ramp extended with a hiss of steam.',
      'Ben adjusted the chin strap of his bicycle helmet,',
      'tucked his trusty, modified pogo stick under one arm,',
      'and stepped out into the alien unknown,',
      'determined to track down his missing cousin.',
    ],
  },
];

/** Music track per cinematic scene. */
export const CINE_TRACK = ['yard', 'lab', 'launch', 'launch', 'cine', 'cine', 'cine', 'cine'];

export const END: BeatScene[] = [
  {
    place: 'The security terminal',
    beats: [
      'The gold USB drive slid into the security terminal with a satisfying click.',
      'Lines of green text raced up the screen.',
      "Mildred's security system blinked, sputtered, and gave up.",
    ],
  },
  {
    place: "Billy's cage",
    beats: [
      'The cage door swung open.',
      'Billy stepped out, straightened his football helmet and grinned.',
      '“Took you long enough, Lieutenant Fizz.”',
    ],
  },
  {
    place: 'Somewhere above',
    beats: [
      'Somewhere above, a hatch slammed.',
      "Mildred McMire's voice echoed down the chocolate halls:",
      "“This isn't over, Ben Blaze! Mortimer and I have plenty more castles to build!”",
    ],
  },
  {
    place: 'Homeward',
    beats: [
      'The cousins raced back to the spaghetti with meatballs flying saucer,',
      'its hold stuffed with every cocoa bean the Zargs had taken.',
      "Earth's chocolate was safe — for now.",
    ],
  },
];

export type DialogueId = 'bossIntro' | 'bossDefeated';
export type Line = [speaker: string, text: string];

/** Indexed by the sim's DIALOGUE event argument (0 = intro, 1 = defeated). */
export const DIALOGUE: Record<DialogueId, Line[]> = {
  bossIntro: [
    [
      'Mildred McMire',
      "Ben Blaze? Mortimer said Billy's little cousin might show up. Cute helmet.",
    ],
    [
      'Mildred McMire',
      'Every cocoa bean on Earth is going into my castle — the biggest chocolate castle in the galaxy! Colossus, squash him!',
    ],
    ['Ben', 'Not before I get Billy back.'],
    [
      'Tip',
      "The Colossus's armour shrugs off fizz. Wait for its dome to pop open after a charge, then strike.",
    ],
  ],
  bossDefeated: [
    ['Mildred McMire', "My Colossus! Fine, keep your silly cousin. I've got a castle to finish!"],
    [
      'Ben',
      "It dropped something… a gold USB drive. There's a security terminal back by the entrance.",
    ],
  ],
};

export interface LevelInfo {
  name: string;
  blurb: string;
  /** Music track while playing it. */
  track: string;
  /** The reverb it is heard in (see `audio/rooms.ts`). */
  room: RoomName;
  /** Shown on the level-cleared card. */
  cleared: string;
}

/** Signposts in Crater Corners, by the text number each sign point carries. */
export const SIGNS: string[] = [
  'Welcome to Crater Corners. Population: nine, plus one very confident cat. Please wipe your boots.',
  'The Sticky Bun Bakery: closed since the Zargs borrowed the cocoa. Sorry, no refunds.',
  'The fountain used to run chocolate. Now it runs on hope and a leaky pipe.',
];

/** Id of the saucer walk-through: a level with no goal, so it never shows a cleared card. */
export const SAUCER_ID = 15;

/** Indexed by level id, matching the `LEVELS` table in the Rust sim. */
export const LEVELS: LevelInfo[] = [
  {
    name: 'Crater Fields',
    blurb: 'Twinkling crystal craters and chocolate pools.',
    track: 'crater',
    room: 'outdoor',
    cleared:
      'The path on to Meteor Mesa is open. Clear it and Zarg Lookout, and the first teleporter will hum.',
  },
  {
    name: 'Crystal Caves',
    blurb: 'Dark, glittering tunnels over chocolate rivers.',
    track: 'caves',
    room: 'cave',
    cleared:
      'One of the two levels the Frosting Frontier teleporter wants is done. Mirror Shafts is the other.',
  },
  {
    name: "Mildred's Citadel",
    blurb: 'A castle of cake, cookie doors and frozen chocolate.',
    track: 'citadel',
    room: 'citadel',
    cleared: 'The Citadel is cleared.',
  },
  {
    name: 'Meteor Mesa',
    blurb: 'Biscuit-rock mesas in open sky, with cloud ledges between them.',
    track: 'sky',
    room: 'outdoor',
    cleared: 'The sky is clear all the way to the horizon. Zarg Lookout is next along the path.',
  },
  {
    name: 'Zarg Lookout',
    blurb: 'A watchtower of ladders, stairs and one slow lift. The red key is on the roof.',
    track: 'tower',
    room: 'tower',
    cleared: 'The first teleporter is humming now, and Marshmallow Meadows are waiting.',
  },
  {
    name: 'Marshmallow Meadows',
    blurb: 'Soft hills, fenced-in marshmallows to bounce off, and a blue key in the clouds.',
    track: 'sky',
    room: 'outdoor',
    cleared: 'Marshmallow fluff clings to Ben’s boots. Somewhere ahead, the music of a playhouse.',
  },
  {
    name: 'Bonbon Playhouse',
    blurb: 'A candy theatre. The walls are only painted flats, so walk right in.',
    track: 'theatre',
    room: 'theatre',
    cleared:
      'The curtain falls on a very sticky stage. That is one of the two levels the teleporter to Rock Candy Reach wants; Marshmallow Meadows is the other.',
  },
  {
    name: 'Fudge Bog',
    blurb: 'Wide pools of hot fudge, stepping stones and three very puffy spore pods.',
    track: 'crater',
    room: 'outdoor',
    cleared: 'Ben wipes fudge off his boots. That was the hard way round, and he knows it.',
  },
  {
    name: 'Mirror Shafts',
    blurb:
      'A tall crystal shaft sealed by gates. Bounce fizz off the mirrors to reach the switches.',
    track: 'caves',
    room: 'shaft',
    cleared:
      'The last gate slides open and the shaft hums. That is one of the two levels the teleporter to the Frosting Frontier wants; the Crystal Caves are the other.',
  },
  {
    name: 'Sugar Glass Gallery',
    blurb: 'A long gallery of hidden rooms. Not everything on these walls is decoration.',
    track: 'theatre',
    room: 'theatre',
    cleared: 'Ben leaves the gallery with a head full of paintings and pockets full of crumbs.',
  },
  {
    name: 'Frosting Flats',
    blurb: 'Long, open stretches of frosted rock, spike runs and hover platforms. Mind the drones.',
    track: 'sky',
    room: 'outdoor',
    cleared: 'A long way across, and not a single frosting stain on Ben’s helmet. Almost.',
  },
  {
    name: 'Frosting Spire',
    blurb: 'The tallest Zarg tower: twelve floors, three lifts, and a key in every colour.',
    track: 'tower',
    room: 'tower',
    cleared:
      'Twelve floors up and twelve floors down, and Ben’s knees have opinions about it. The Citadel needs both the Spire and the Foundry.',
  },
  {
    name: 'Cocoa Foundry',
    blurb: 'Belts, presses and pools of molten metal. Mind the timing, and mind the belts.',
    track: 'foundry',
    room: 'foundry',
    cleared:
      'The presses thump on without him. Ben dusts the soot off his helmet. The Citadel needs both the Spire and the Foundry.',
  },
  {
    name: 'Gumdrop Isle',
    blurb: 'A secret island, covered in snacks. Nobody was supposed to find this place.',
    track: 'secret',
    room: 'outdoor',
    cleared:
      'Ben leaves the island with a few more lives than he came with, and a very full pocket.',
  },
  {
    name: 'Whisper Hollow',
    blurb: 'A cave for climbers: a vine rope over fudge, a chimney to kick up, and hanging vines.',
    track: 'caves',
    room: 'cave',
    cleared:
      'Ben’s arms ache and his boots are scuffed. The old paintings never mentioned the climb, but somebody grew those vines on purpose.',
  },
  {
    name: 'The Saucer',
    blurb: 'Ben’s own flying saucer, parked and steaming. Nothing to clear; just a look around.',
    track: 'secret',
    room: 'tower',
    cleared: '',
  },
];
